import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { wasteCollectionApi, WasteCollectionFormDTO } from '../api/waste-collection.api';
import { getErrorMessage } from '@/core/api/response-handler';

/**
 * Hook for managing waste order mutations (update, validate, reject, logistics)
 */
export function useWasteOrdersMutations() {
    const queryClient = useQueryClient();

    /**
     * Mutation for updating order details (admin)
     */
    const updateOrder = useMutation({
        mutationFn: async (data: WasteCollectionFormDTO) => {
            return wasteCollectionApi.adminUpdate(data);
        },
        onSuccess: () => {
            toast.success('Comanda a fost actualizată cu succes');
            // Invalidate both the report and individual order queries
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la actualizarea comenzii'));
        }
    });

    /**
     * Mutation for approving an order
     */
    const approveOrder = useMutation({
        mutationFn: async (orderId: string) => {
            return wasteCollectionApi.updateValidation(orderId, { isValidated: true });
        },
        onSuccess: () => {
            toast.success('Comanda a fost validată cu succes');
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la validarea comenzii'));
        }
    });

    /**
     * Mutation for rejecting an order
     */
    const rejectOrder = useMutation({
        mutationFn: async ({ orderId, reason }: { orderId: string; reason: string }) => {
            return wasteCollectionApi.updateValidation(orderId, {
                isValidated: false,
                rejectReason: reason
            });
        },
        onSuccess: () => {
            toast.success('Comanda a fost respinsă');
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la respingerea comenzii'));
        }
    });

    /**
     * Mutation for updating logistics data (real values and documents)
     */
    const updateLogistics = useMutation({
        mutationFn: async ({
            orderId,
            data
        }: {
            orderId: string;
            data: {
                realDeeeKg?: number;
                realDbaKg?: number;
                realBecuriSiNeoaneKg?: number;
                proceseVerbale?: string[];
            }
        }) => {
            return wasteCollectionApi.updateLogistics(orderId, data);
        },
        onSuccess: () => {
            toast.success('Valorile reale au fost actualizate cu succes');
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la actualizarea valorilor reale'));
        }
    });

    /**
     * Mutation for marking order as processed/transmitted
     */
    const markAsProcessed = useMutation({
        mutationFn: async (orderId: string) => {
            return wasteCollectionApi.markProcessed(orderId);
        },
        onSuccess: () => {
            toast.success('Comanda a fost marcată ca procesată');
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la marcarea comenzii ca procesată'));
        }
    });

    /**
     * Mutation for deleting an order
     */
    const deleteOrder = useMutation({
        mutationFn: async (orderId: string) => {
            return wasteCollectionApi.delete(orderId);
        },
        onSuccess: () => {
            toast.success('Comanda a fost ștearsă cu succes');
            queryClient.invalidateQueries({ queryKey: ['waste-orders'] });
        },
        onError: (error) => {
            toast.error(getErrorMessage(error, 'Eroare la ștergerea comenzii'));
        }
    });

    return {
        // Mutations
        updateOrder,
        approveOrder,
        rejectOrder,
        updateLogistics,
        markAsProcessed,
        deleteOrder,

        // Loading states
        isUpdating: updateOrder.isPending,
        isApproving: approveOrder.isPending,
        isRejecting: rejectOrder.isPending,
        isUpdatingLogistics: updateLogistics.isPending,
        isMarkingProcessed: markAsProcessed.isPending,
        isDeleting: deleteOrder.isPending,

        // General loading state
        isLoading: updateOrder.isPending ||
                   approveOrder.isPending ||
                   rejectOrder.isPending ||
                   updateLogistics.isPending ||
                   markAsProcessed.isPending ||
                   deleteOrder.isPending
    };
}